import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImageIcon, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button as PressableButton } from "react-aria-components";

import { Button } from "@/components/base/button";
import { addToast } from "@/components/base/toast";
import { useTRPC } from "@/integrations/trpc/react";

type FileEntry = {
  file: File;
  preview: string;
  status: "pending" | "uploading" | "done" | "error";
};

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const CONCURRENT_UPLOADS = 4;
const BATCH_SIZE = 50;

export function BulkUploader({
  tournamentId,
  onComplete,
}: {
  tournamentId: number;
  onComplete: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dirInputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  const { mutateAsync: getTokens } = useMutation(
    trpc.gallery.getUploadTokens.mutationOptions(),
  );
  const { mutateAsync: savePhotos } = useMutation(
    trpc.gallery.savePhotos.mutationOptions(),
  );

  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, []);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const imageFiles = Array.from(newFiles).filter((f) =>
      f.type.startsWith("image/"),
    );
    const entries: FileEntry[] = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      status: "pending" as const,
    }));
    setFiles((prev) => [...prev, ...entries]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const uploadAll = async () => {
    const pending = files.filter((f) => f.status === "pending");
    if (pending.length === 0) return;

    setIsUploading(true);
    let totalUploaded = 0;
    let totalFailed = 0;

    try {
      // Process in batches of BATCH_SIZE
      for (let i = 0; i < pending.length; i += BATCH_SIZE) {
        const batch = pending.slice(i, i + BATCH_SIZE);

        // Get signed upload tokens for this batch
        const tokens = await getTokens({
          tournamentId,
          files: batch.map((f) => ({ filename: f.file.name })),
        });

        // Upload files with concurrency limit
        const uploaded: {
          storagePath: string;
          filename: string;
          fileSize: number;
        }[] = [];
        const indexedQueue = tokens.map((token, idx) => ({ token, idx }));

        const uploadNext = async () => {
          while (indexedQueue.length > 0) {
            const item = indexedQueue.shift()!;
            const token = item.token;
            const entry = batch[item.idx];
            if (!entry) continue;

            // Update status
            setFiles((prev) =>
              prev.map((f) =>
                f === entry ? { ...f, status: "uploading" as const } : f,
              ),
            );

            try {
              const res = await fetch(token.uploadUrl, {
                method: "PUT",
                headers: {
                  authorization: `Bearer ${ANON_KEY}`,
                  "content-type": entry.file.type,
                },
                body: entry.file,
              });

              if (!res.ok) throw new Error(`Upload failed: ${res.status}`);

              uploaded.push({
                storagePath: token.storagePath,
                filename: token.filename,
                fileSize: entry.file.size,
              });

              totalUploaded++;
              setFiles((prev) =>
                prev.map((f) =>
                  f === entry ? { ...f, status: "done" as const } : f,
                ),
              );
            } catch {
              totalFailed++;
              setFiles((prev) =>
                prev.map((f) =>
                  f === entry ? { ...f, status: "error" as const } : f,
                ),
              );
            }
          }
        };

        // Run concurrent uploads
        await Promise.all(
          Array.from({ length: CONCURRENT_UPLOADS }, () => uploadNext()),
        );

        // Save successfully uploaded photos to database
        if (uploaded.length > 0) {
          await savePhotos({ tournamentId, photos: uploaded });
        }
      }

      await queryClient.invalidateQueries(
        trpc.gallery.getPhotos.queryOptions({ tournamentId }),
      );
      await queryClient.invalidateQueries(
        trpc.gallery.hasPhotos.queryOptions({ tournamentId }),
      );

      if (totalFailed === 0) {
        addToast({
          variant: "success",
          title: "Upload complete",
          description: `${totalUploaded} photo${totalUploaded !== 1 ? "s" : ""} uploaded. An admin will need to approve them before they're visible.`,
        });
        onComplete();
      } else if (totalUploaded > 0) {
        addToast({
          variant: "error",
          title: "Some uploads failed",
          description: `${totalUploaded} uploaded, ${totalFailed} failed. You can retry the failed photos.`,
        });
      } else {
        addToast({
          variant: "error",
          title: "Upload failed",
          description: `All ${totalFailed} uploads failed.`,
        });
      }
    } catch (err) {
      addToast({
        variant: "error",
        title: "Upload failed",
        description: (err as Error).message,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const doneCount = files.filter((f) => f.status === "done").length;
  const errorCount = files.filter((f) => f.status === "error").length;

  return (
    <div className="flex flex-col gap-4">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files && addFiles(e.target.files)}
      />
      <input
        ref={dirInputRef}
        type="file"
        // @ts-expect-error -- webkitdirectory is not in the type
        webkitdirectory=""
        multiple
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files && addFiles(e.target.files)}
      />

      {/* Drop zone */}
      {files.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center transition-colors hover:border-gray-400"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <ImageIcon size={32} className="text-gray-400" />
          <p className="text-sm text-gray-600">Drag and drop photos here, or</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onPress={() => fileInputRef.current?.click()}
            >
              Select Files
            </Button>
            <Button
              variant="outline"
              size="sm"
              onPress={() => dirInputRef.current?.click()}
            >
              Select Folder
            </Button>
          </div>
        </div>
      )}

      {/* File preview grid */}
      {files.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {files.length} photo{files.length !== 1 ? "s" : ""} selected
              {doneCount > 0 && ` (${doneCount} uploaded)`}
              {errorCount > 0 && ` (${errorCount} failed)`}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onPress={() => fileInputRef.current?.click()}
                isDisabled={isUploading}
              >
                Add More
              </Button>
              {!isUploading && (
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    files.forEach((f) => URL.revokeObjectURL(f.preview));
                    setFiles([]);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {files.map((entry, i) => (
              <div key={entry.preview} className="group relative aspect-square">
                <img
                  src={entry.preview}
                  alt={entry.file.name}
                  className="h-full w-full rounded-md object-cover"
                />
                {entry.status === "uploading" && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/40">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  </div>
                )}
                {entry.status === "done" && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md bg-green-500/30">
                    <span className="text-lg text-white">✓</span>
                  </div>
                )}
                {entry.status === "error" && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md bg-red-500/30">
                    <span className="text-lg text-white">✗</span>
                  </div>
                )}
                {entry.status === "pending" && !isUploading && (
                  <PressableButton
                    onPress={() => removeFile(i)}
                    className="absolute top-1 right-1 rounded-full bg-black/50 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70"
                  >
                    <X size={14} />
                  </PressableButton>
                )}
              </div>
            ))}
          </div>

          <Button
            variant="solid"
            onPress={uploadAll}
            isDisabled={isUploading || pendingCount === 0}
          >
            <Upload size={16} />
            {isUploading
              ? `Uploading... (${doneCount}/${files.length})`
              : `Upload ${pendingCount} Photo${pendingCount !== 1 ? "s" : ""}`}
          </Button>
        </>
      )}
    </div>
  );
}
